import styled from "@emotion/styled";
import { SPRING_ANIMATION, Z_INDICES } from "config/consts";
import { motion } from "framer-motion";
import { noop } from "lodash-es";
import React, {
  CSSProperties,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GlobalStore } from "store/global/GlobalStore";
import { handleGetChartData } from "utils/tokenSelectorUtils";
import { TokenSelectorRow } from "./elements/TokenSelectorDropdownRow";
import { CounterParty } from "utils/tradeUtils";
import { Amount } from "types/amount";
import { Hash } from "viem";
import { getWrappedTokenAddress } from "../../utils/chainUtils";
import { TokenSelectorPreview } from "./elements/TokenSelectorPreview";
import { CannotFindTokenInfo } from "./elements/CantFindTokenInfo";
import { useIsMobileOrTablet } from "../../hooks/useDeviceType";
import { useFlyoutMenu } from "../../hooks/useFlyoutMenu";
import { TokenListMetadata } from "../../hooks/queries/tokens-queries";
import { TokenList } from "../../store/global/getAllTokenLists";

interface TokenSelectorDropdownProps {
  isHidden: boolean;
  counterParty: CounterParty;
  searchString: string;
  tokensToShow: Array<Hash>;
  highlightedRowIdx: number | null;
  scrollToTop: () => void;
  closeDropdown: () => void;
  isChangingToken?: Hash | undefined;
  setHighlightedRowIdx: React.Dispatch<React.SetStateAction<number | null>>;
  tokenList: TokenList | undefined;
  tokenListMetadata: TokenListMetadata | undefined;
}

export const TokenSelectorDropdown: React.FC<TokenSelectorDropdownProps> = ({
  isHidden,
  counterParty,
  searchString,
  tokensToShow,
  highlightedRowIdx,
  scrollToTop,
  setHighlightedRowIdx,
  closeDropdown,
  isChangingToken,
  tokenList,
  tokenListMetadata,
}) => {
  const balances = GlobalStore.useState(s => s.balances);
  const address = GlobalStore.useState(s => s.address);
  const network = GlobalStore.useState(s => s.network);
  const chartLists = GlobalStore.useState(s => s.chartLists);
  const isMobileOrTablet = useIsMobileOrTablet();
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const { handleMouseMove, handleContainerMouseLeave } = useFlyoutMenu({
    setHighlightedRowIdx: setHighlightedRowIdx,
    highlightedRowIdx: highlightedRowIdx,
    flyoutEl: placeholderRef.current,
  });
  const balanceTokenAmountMap = useMemo(
    () =>
      balances
        .map(entry => entry.tokenAddress)
        .reduce<Record<Hash, Amount>>(
          (prev, curr: Hash, i) => {
            prev[curr] = balances[i].balance;
            return prev;
          },
          {} as Record<Hash, Amount>
        ),
    [balances]
  );
  const firstTokenWithoutBalanceIdx = tokensToShow?.findIndex(symbol =>
    balanceTokenAmountMap[symbol].value.eq(0)
  );

  const getDropdownHeight = useCallback(
    (rowCount: number) => Math.min(MAX_DROPDOWN_HEIGHT, rowCount * TOKEN_ROW_HEIGHT + 8 * 2),
    []
  );

  const rowCount = tokensToShow?.length ?? 0;
  const [dropdownHeight, setDropdownHeight] = useState<CSSProperties["height"]>(
    isHidden ? 0 : getDropdownHeight(rowCount)
  );
  useLayoutEffect(() => {
    let height: CSSProperties["height"] = getDropdownHeight(rowCount);
    if (rowCount === 0) {
      height = "fit-content";
    } else if (isHidden) {
      height = 0;
    }
    setDropdownHeight(height);
  }, [getDropdownHeight, isHidden, rowCount]);

  const handleMouseEnterRow = useCallback(
    (tokenAddress: Hash) => {
      handleGetChartData(tokenAddress, network, chartLists);
    },
    [chartLists, network]
  );

  if (tokenList === undefined || (address !== "" && balances.length === 0)) {
    const rowCount = 7;
    return (
      <Container
        style={{
          maxHeight: getDropdownHeight(MAX_ROWS_VISIBLE),
        }}
        initial={{ height: 0 }}
        animate={{ height: Math.min(357, rowCount * TOKEN_ROW_HEIGHT + 8 * 2) }}
        transition={{ duration: 0.16, ease: "easeOut" }}
        isHidden={isHidden}
      >
        {Array(rowCount)
          .fill(0)
          .map((_, i) => (
            <TokenSelectorRow
              key={i}
              tokenAddress={getWrappedTokenAddress(network)}
              isLoading={true}
              showSeparatorAbove={false}
              counterParty={"maker"}
              closeDropdown={noop}
              isFirst={i === 0}
              setHighlightedRowIdx={setHighlightedRowIdx}
              scrollToTop={scrollToTop}
              isChangingToken={isChangingToken}
              tokenList={tokenList}
              handleDiagonalMouseMove={noop}
              isLastHighlightedWithKeyboard={false}
              handleMouseEnterRow={noop}
              tokenListMetadata={tokenListMetadata}
            />
          ))}
      </Container>
    );
  }

  return (
    <div onMouseLeave={() => handleContainerMouseLeave()}>
      <Container
        style={{
          maxHeight: getDropdownHeight(MAX_ROWS_VISIBLE),
        }}
        initial={{ height: 0 }}
        animate={{
          height: dropdownHeight,
          ...(rowCount === 0 ? { minHeight: `${CANNOT_FIND_TOKEN_INFO_MIN_HEIGHT}px` } : undefined),
        }}
        transition={SPRING_ANIMATION}
        isHidden={isHidden}
      >
        <SubContainer>
          {tokensToShow.length === 0 ? (
            <CannotFindTokenInfo searchString={searchString} />
          ) : (
            <>
              {tokensToShow.map((tokenAddress, i) => (
                <TokenSelectorRow
                  key={tokenAddress}
                  tokenAddress={tokenAddress}
                  isHighlighted={highlightedRowIdx != null && i === highlightedRowIdx}
                  showSeparatorAbove={
                    firstTokenWithoutBalanceIdx !== 0 &&
                    i === firstTokenWithoutBalanceIdx &&
                    searchString === ""
                  }
                  setHighlightedRowIdx={setHighlightedRowIdx}
                  handleDiagonalMouseMove={e => handleMouseMove(i, e)}
                  counterParty={counterParty}
                  closeDropdown={closeDropdown}
                  isFirst={i === 0}
                  scrollToTop={scrollToTop}
                  isChangingToken={isChangingToken}
                  tokenList={tokenList}
                  isLastHighlightedWithKeyboard={i === rowCount - 1}
                  handleMouseEnterRow={() => handleMouseEnterRow(tokenAddress)}
                  tokenListMetadata={tokenListMetadata}
                />
              ))}
            </>
          )}
        </SubContainer>
      </Container>
      <TokenSelectorPreviewsContainer isHidden={isHidden}>
        <div ref={placeholderRef} style={{ visibility: "hidden", position: "absolute" }}>
          Placeholder
        </div>
        {tokensToShow.map((tokenAddress, i) => (
          <TokenSelectorPreview
            key={i}
            isVisible={highlightedRowIdx != null && i === highlightedRowIdx && !isMobileOrTablet}
            tokenAddress={tokenAddress}
            tokenList={tokenList}
            chain={network}
            isLoading={tokensToShow == null}
            tokenListMetadata={tokenListMetadata}
          />
        ))}
      </TokenSelectorPreviewsContainer>
    </div>
  );
};
TokenSelectorDropdown.whyDidYouRender = true;
// Helpers

const TOKEN_ROW_HEIGHT = 61;
const CANNOT_FIND_TOKEN_INFO_MIN_HEIGHT = 201;
const MAX_ROWS_VISIBLE = 7;
export const MAX_DROPDOWN_HEIGHT = 357;

const SubContainer = styled.div`
  position: relative;
  overflow: scroll;
  padding: 8px;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const TokenSelectorPreviewsContainer = styled.div<{ isHidden: boolean }>`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 8px;
  left: 97%;
  z-index: 9999999;
  position: absolute;
  visibility: ${props => (props.isHidden ? "hidden" : "initial")};
`;

const Container = styled(motion.div)<{ isHidden: boolean }>(({ theme, isHidden }) => {
  return {
    position: "absolute",
    zIndex: Z_INDICES.CONTENT_SURFACE,
    visibility: isHidden ? "hidden" : "initial",
    left: 8.5,
    display: "flex",
    flexDirection: "column",
    minWidth: "min(376px, 95%)",
    width: "min(376px, 80%)",
    background: theme.surface.main.secondary.default,
    borderRadius: 24,
    overflow: "hidden",
    msOverflowStyle: "none" /* IE and Edge */,
    scrollbarWidth: "none" /* Firefox */,

    "::-webkit-scrollbar": {
      display: "none" /* Chrome, Safari and Opera */,
    },
  };
});
